import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { RootState } from './store';
import { baseUrlApi } from '../../utils/url';

export interface BailComplementaire {
  id?: number;
  LeaseId: number;
  bail_duree: '1an' | '2ans' | '3ans' | '6ans';
  preavis_locataire: '1mois' | '3mois';
  index_revision: 'IRL' | 'ICC';
  irl_trimestre: number | null;
  irl_annee: number | null;
  irl_valeur: number | null;
  depot_garantie: '1mois' | '2mois';
  baileur_type: 'physique' | 'morale';
  colocation: boolean;
  sous_location: boolean;
  animaux: boolean;
  travaux: 'aucun' | 'locataire' | 'bailleur';
  clause_resolutoire: boolean;
  caution_solidaire: boolean;
  agence: boolean;
  zone_tendue: boolean;
  copropriete: boolean;
  paiement_mode: 'virement' | 'cheque' | 'prelevement' | 'especes';
  meuble: boolean;
  charges_reelles: boolean;
  dpe_classe: string | null;
  ges_classe: string | null;
  elec_ancien: boolean;
  gaz_absent: boolean;
  avant_1949: boolean;
  avant_1997: boolean;
}

export const DEFAULT_BAIL_COMPLEMENTAIRE: Omit<BailComplementaire, 'LeaseId'> = {
  bail_duree: '3ans',
  preavis_locataire: '3mois',
  index_revision: 'IRL',
  irl_trimestre: null,
  irl_annee: null,
  irl_valeur: null,
  depot_garantie: '1mois',
  baileur_type: 'physique',
  colocation: false,
  sous_location: false,
  animaux: false,
  travaux: 'aucun',
  clause_resolutoire: true,
  caution_solidaire: false,
  agence: false,
  zone_tendue: false,
  copropriete: true,
  paiement_mode: 'virement',
  meuble: false,
  charges_reelles: false,
  dpe_classe: null,
  ges_classe: null,
  elec_ancien: false,
  gaz_absent: false,
  avant_1949: false,
  avant_1997: false,
};

interface BailComplementaireState {
  data: BailComplementaire | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
}

const initialState: BailComplementaireState = {
  data: null,
  loading: false,
  saving: false,
  error: null,
};

const headers = (token: string) => ({ Authorization: `bearer ${token}` });

export const fetchBailComplementaire = createAsyncThunk<BailComplementaire | null, number, { state: RootState }>(
  'bailComplementaire/fetch',
  async (leaseId, { getState, rejectWithValue }) => {
    try {
      const token = getState().authentification.user.token;
      const res = await axios.get(`${baseUrlApi}/bailcomplementaire/${leaseId}`, { headers: headers(token) });
      return Object.keys(res.data).length === 0 ? null : res.data;
    } catch (e: any) {
      return rejectWithValue(e.response?.data?.message || 'Erreur chargement');
    }
  }
);

export const saveBailComplementaire = createAsyncThunk<BailComplementaire, BailComplementaire, { state: RootState }>(
  'bailComplementaire/save',
  async (data, { getState, rejectWithValue }) => {
    try {
      const token = getState().authentification.user.token;
      const res = await axios.put(`${baseUrlApi}/bailcomplementaire/${data.LeaseId}`, data, { headers: headers(token) });
      return res.data;
    } catch (e: any) {
      return rejectWithValue(e.response?.data?.message || 'Erreur sauvegarde');
    }
  }
);

const bailComplementaireSlice = createSlice({
  name: 'bailComplementaire',
  initialState,
  reducers: {
    clearBailComplementaire: (state) => {
      state.data = null;
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchBailComplementaire.pending,   s => { s.loading = true; s.error = null; })
      .addCase(fetchBailComplementaire.fulfilled, (s, a) => { s.loading = false; s.data = a.payload; })
      .addCase(fetchBailComplementaire.rejected,  (s, a) => { s.loading = false; s.error = a.payload as string; })
      .addCase(saveBailComplementaire.pending,    s => { s.saving = true; s.error = null; })
      .addCase(saveBailComplementaire.fulfilled,  (s, a) => { s.saving = false; s.data = a.payload; })
      .addCase(saveBailComplementaire.rejected,   (s, a) => { s.saving = false; s.error = a.payload as string; });
  },
});

export const { clearBailComplementaire } = bailComplementaireSlice.actions;
export default bailComplementaireSlice.reducer;
